import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { gql, useQuery } from '@zenbusiness/application-commons-graphql';
import { useError } from '@zenbusiness/application-commons-logging';

import { RELEASE_RA_V2 } from '#client/constants/experimentsFlags';

const ADDRESS_FIELDS = gql`
  fragment addressFields on BusinessAddress {
    address1
    address2
    city
    state
    zip
  }
`;

export const GET_BUSINESS_ENTITY_QUERY = gql`
  query BusinessEntityProfileQuery($businessEntityUuid: UUID!) {
    businessEntity: businessEntity(businessEntityUuid: $businessEntityUuid) {
      uuid
      name
      type
      domesticJurisdiction
      createdAt
      updatedAt
      formationDate
      ein
      addresses {
        mailing {
          ...addressFields
        }
        principal {
          ...addressFields
        }
      }
    }
  }
  ${ADDRESS_FIELDS}
`;

const REGISTERED_AGENT_FIELDS = gql`
  fragment registeredAgentFields on RegisteredAgent {
    uuid
    contact {
      fullName
      addresses {
        type
        addressLine1
        addressLine2
        city
        state
        zip
      }
    }
  }
`;

export const GET_REGISTERED_AGENTS_QUERY = gql`
  query RegisteredAgentsProfileQuery($businessEntityUuid: UUID!) {
    registeredAgents: registeredAgents(businessEntityUuid: $businessEntityUuid) {
      ...registeredAgentFields
    }
  }
  ${REGISTERED_AGENT_FIELDS}
`;

/**
 * PROVISIONAL — remove once `release-ra-v2` is fully enabled.
 *
 * `registeredAgentsV1` reads the pre-v2 source (zenapi) and returns the same shape, so it shares the
 * fragment above and is aliased to `registeredAgents`. That keeps the response key identical between
 * the two, and everything downstream of this hook source-agnostic.
 */
export const GET_REGISTERED_AGENTS_V1_QUERY = gql`
  query RegisteredAgentsProfileV1Query($businessEntityUuid: UUID!) {
    registeredAgents: registeredAgentsV1(businessEntityUuid: $businessEntityUuid) {
      ...registeredAgentFields
    }
  }
  ${REGISTERED_AGENT_FIELDS}
`;

export function useBusinessProfile() {
  const { businessEntityUuid } = useBusinessEntity();

  const {
    data: businessEntityData,
    loading: entityLoading,
    error: entityError,
  } = useQuery(GET_BUSINESS_ENTITY_QUERY, {
    noThrow: true,
    variables: {
      businessEntityUuid,
    },
  });

  // PROVISIONAL — drop the flag and inline the v2 query once RA v2 is fully rolled out.
  // graphql-commons cannot read a flag, so the source is chosen here by swapping the document.
  const raV2Enabled = useExperimentFlag(RELEASE_RA_V2, false);

  const {
    data: registeredAgentsData,
    loading: agentsLoading,
    error: agentsError,
  } = useQuery(raV2Enabled ? GET_REGISTERED_AGENTS_QUERY : GET_REGISTERED_AGENTS_V1_QUERY, {
    noThrow: true,
    variables: {
      businessEntityUuid,
    },
  });

  useError(entityError);
  useError(agentsError);

  return {
    data: {
      businessEntity: businessEntityData?.businessEntity,
      registeredAgents: registeredAgentsData?.registeredAgents,
    },
    error: entityError,
    loading: entityLoading,
    registeredAgentsLoading: agentsLoading,
    registeredAgentsError: !!agentsError,
  };
}
